import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { t } from '@/i18n';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useUsersApi, DirectoryUnavailableError, type DirectoryUser } from '@/services/api';

interface EntraUserPickerProps {
  value: DirectoryUser | null;
  onSelect: (user: DirectoryUser) => void;
  /** Directory answered 503 DIRECTORY_UNAVAILABLE — parent falls back to manual e-mail entry. */
  onUnavailable: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * Searchable Entra directory combobox for the add-user dialog (same
 * Popover+Command pattern as ContractorSelector, but with debounced
 * server-side search — the tenant directory is not loaded up front).
 */
export function EntraUserPicker({ value, onSelect, onUnavailable }: EntraUserPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchDirectory } = useUsersApi();
  // Ignore out-of-order responses from earlier keystrokes.
  const requestSeq = useRef(0);

  useEffect(() => {
    const query = search.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const seq = ++requestSeq.current;
    const timer = setTimeout(() => {
      searchDirectory(query)
        .then((users) => {
          if (seq !== requestSeq.current) return;
          setResults(users);
          setLoading(false);
        })
        .catch((error) => {
          if (seq !== requestSeq.current) return;
          setLoading(false);
          if (error instanceof DirectoryUnavailableError) {
            setOpen(false);
            onUnavailable();
            return;
          }
          console.error('Directory search failed:', error);
          setResults([]);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchDirectory]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {value ? (
              <>
                {value.displayName || value.email}
                <span className="text-muted-foreground ml-1.5 text-xs">{value.email}</span>
              </>
            ) : (
              <span className="text-muted-foreground">{t('users.form.searchDirectory')}</span>
            )}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('users.form.searchDirectoryPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {loading ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-sm">
                <Loader2 className="size-4 animate-spin" />
              </div>
            ) : (
              <>
                <CommandEmpty>
                  {search.trim().length < MIN_QUERY_LENGTH
                    ? t('users.form.searchDirectoryHint')
                    : t('users.form.directoryNoResults')}
                </CommandEmpty>
                <CommandGroup>
                  {results.map((user) => (
                    <CommandItem
                      key={user.entraOid}
                      value={user.entraOid}
                      onSelect={() => {
                        onSelect(user);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn('size-4', value?.entraOid === user.entraOid ? 'opacity-100' : 'opacity-0')}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm">
                          {user.displayName || user.email}
                          <span className="text-muted-foreground ml-1.5 text-xs">{user.email}</span>
                        </p>
                        {(user.jobTitle || user.department) && (
                          <p className="text-muted-foreground truncate text-xs">
                            {[user.jobTitle, user.department].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
